// src/utils/dateUtils.ts
// Timezone-aware date formatting using company settings
// Default: Africa/Lagos (WAT, UTC+1)

const DEFAULT_TZ = 'Africa/Lagos';
const DEFAULT_LOCALE = 'en-NG';

/**
 * Format date only: "14 May 2026"
 */
export const formatDate = (
  date: string | Date | null | undefined,
  timezone = DEFAULT_TZ
): string => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString(DEFAULT_LOCALE, {
      timeZone: timezone,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(date);
  }
};

/**
 * Format time only: "11:52"
 */
export const formatTime = (
  date: string | Date | null | undefined,
  timezone = DEFAULT_TZ
): string => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleTimeString(DEFAULT_LOCALE, {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(date);
  }
};

/**
 * Format date + time: "14 May 2026, 11:52"
 */
export const formatDateTime = (
  date: string | Date | null | undefined,
  timezone = DEFAULT_TZ
): string => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString(DEFAULT_LOCALE, {
      timeZone: timezone,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(date);
  }
};

/**
 * Format short: "14 May, 11:52" (no year — for tables/lists)
 */
export const formatShort = (
  date: string | Date | null | undefined,
  timezone = DEFAULT_TZ
): string => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString(DEFAULT_LOCALE, {
      timeZone: timezone,
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(date);
  }
};

/**
 * Format date only short: "14 May" (no year)
 */
export const formatDateShort = (
  date: string | Date | null | undefined,
  timezone = DEFAULT_TZ
): string => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString(DEFAULT_LOCALE, {
      timeZone: timezone,
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return String(date);
  }
};

/**
 * Format for portal/public pages: "14 May, 11:52"
 * Uses en-NG locale with Africa/Lagos default
 */
export const formatPortalDate = (
  date: string | Date | null | undefined,
  timezone = DEFAULT_TZ
): string => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString(DEFAULT_LOCALE, {
      timeZone: timezone,
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(date);
  }
};